<!-- /var/www/html/ts3-manager/packages/ui/src/components/ServerBanner.vue -->
<template>
  <v-container fluid>
    <v-row dense>
      <!-- LEFT: Image Gallery -->
      <v-col cols="12" md="3">
        <v-card>
          <v-card-title class="py-3">
            <span class="text-subtitle-1 font-weight-medium">Görseller</span>
            <v-spacer />

            <v-btn
              icon
              @click="reloadImages"
              :loading="loadingImages"
              title="Yenile"
            >
              <v-icon>refresh</v-icon>
            </v-btn>
          </v-card-title>

          <v-divider />

          <v-card-text style="max-height: 85vh; overflow: auto">
            <v-alert type="info" dense text class="mb-3">
              <b>banner_*.jpg/png</b> arkaplan olarak döner. Overlay:
              <b>banner.png</b>
            </v-alert>
            <!-- UPLOAD -->
            <v-card outlined class="pa-3">
              <div class="text-subtitle-2 mb-2">Resim Yükle</div>

              <v-file-input
                v-model="uploadFile"
                label="PNG / JPG seç"
                dense
                outlined
                accept="image/png,image/jpeg"
                prepend-icon="mdi-image"
              />

              <v-btn
                block
                color="primary"
                :disabled="!uploadFile"
                :loading="uploading"
                @click="uploadImage"
              >
                <v-icon left>cloud_upload</v-icon> Yükle
              </v-btn>
            </v-card>
            <v-text-field
              v-model="imageFilter"
              label="Ara"
              dense
              outlined
              clearable
              prepend-inner-icon="search"
              class="mb-2"
            />

            <!-- SINGLE COLUMN 345x128 -->
            <!-- Filtre yoksa: drag&drop aktif -->
            <draggable
              v-if="!imageFilter"
              v-model="images"
              handle=".drag-handle"
              :animation="150"
              ghost-class="drag-ghost"
              chosen-class="drag-chosen"
              drag-class="drag-dragging"
              @end="saveImageOrder"
            >
              <div
                v-for="img in filteredImages"
                :key="img.name"
                class="mb-3"
                :ref="'thumb_' + img.name"
              >
                <v-card
                  outlined
                  class="pa-2 d-flex flex-column banner-thumb"
                  @click="selectBg(img)"
                  style="cursor: pointer"
                  :class="{
                    'elevation-8': selectedBg === img.name,
                    'banner-flash': flashBg === img.name,
                  }"
                >
                  <div class="d-flex align-center mb-2">
                    <v-icon
                      class="mr-2 drag-handle"
                      style="cursor: grab; user-select: none"
                    >
                      drag_indicator
                    </v-icon>
                    <div class="caption text-truncate" style="max-width: 250px">
                      {{ img.name }}
                    </div>
                    <v-spacer />
                    <v-btn
                      icon
                      small
                      color="error"
                      title="Sil"
                      @click.stop="deleteImage(img)"
                    >
                      <v-icon small>delete</v-icon>
                    </v-btn>
                  </div>

                  <v-img
                    :src="img.url"
                    width="345"
                    height="128"
                    contain
                    style="border-radius: 10px; pointer-events: none"
                  />
                </v-card>
              </div>
            </draggable>

            <!-- Filtre varsa: normal liste (drag yok) -->
            <div v-else>
              <div v-for="img in filteredImages" :key="img.name" class="mb-3">
                <v-card
                  outlined
                  class="pa-2"
                  @click="selectBg(img)"
                  style="cursor: pointer"
                  :class="{ 'elevation-8': selectedBg === img.name }"
                >
                  <v-img :src="img.url" width="345" height="128" contain />
                  <div class="d-flex align-center mt-2">
                    <div class="caption text-truncate" style="max-width: 250px">
                      {{ img.name }}
                    </div>
                    <v-spacer />
                    <v-btn
                      icon
                      small
                      color="error"
                      title="Sil"
                      @click.stop="deleteImage(img)"
                    >
                      <v-icon small>delete</v-icon>
                    </v-btn>
                  </div>
                </v-card>
              </div>
            </div>

            <v-divider class="my-3" />
          </v-card-text>
        </v-card>
      </v-col>

      <!-- MIDDLE: Preview + Quick Add -->
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title class="py-3">
            <span class="text-subtitle-1 font-weight-medium"
              >Canlı Önizleme</span
            >

            <span
              v-if="currentPreviewBgName"
              class="caption text-truncate ml-3"
              style="
                max-width: 420px;
                opacity: 0.9;
                cursor: pointer;
                text-decoration: underline;
              "
              :title="'Solda bul: ' + currentPreviewBgName"
              @click="scrollToPreviewBg"
            >
              ({{ currentPreviewBgName }})
            </span>

            <v-spacer />
            <v-btn icon @click="refreshPreview" title="Önizlemeyi yenile">
              <v-icon>cached</v-icon>
            </v-btn>
          </v-card-title>

          <v-divider />

          <v-card-text>
            <v-alert v-if="error" type="error" dense text class="mb-3">{{
              error
            }}</v-alert>

            <v-card outlined class="pa-2">
              <img
                :key="String(previewTick)"
                :src="previewImgSrc"
                alt="banner preview"
                style="width: 100%; height: auto; display: block"
              />
            </v-card>
          </v-card-text>
        </v-card>
        <v-card class="mt-3" outlined>
          <v-card-title class="py-3">
            <span class="text-subtitle-1 font-weight-medium"
              >TS3 Banner Linki</span
            >
            <v-spacer />
            <v-btn small @click="copyBannerUrl">
              <v-icon left small>content_copy</v-icon> Kopyala
            </v-btn>
          </v-card-title>

          <v-divider />

          <v-card-text>
            <v-text-field
              :value="publicBannerUrl"
              label="TeamSpeak banner URL"
              dense
              outlined
              readonly
              append-icon="open_in_new"
              @click:append="openBannerUrl"
            />
            <div class="text--secondary" style="font-size: 12px">
              TS3 sunucunda <b>virtualserver_hostbanner_url</b> veya panelde
              Banner URL alanına bunu yaz.
            </div>
          </v-card-text>
        </v-card>

        <!-- ORTA: ALANLAR (alt alta satırlar) -->
        <v-card class="mt-3">
          <v-card-title class="py-3">
            <span class="text-subtitle-1 font-weight-medium">Alanlar</span>
            <v-spacer />
            <v-btn color="primary" @click="addFieldRow">
              <v-icon left>add</v-icon> Yeni Alan
            </v-btn>
          </v-card-title>

          <v-divider />

          <v-card-text>
            <v-alert v-if="!fieldRows.length" type="info" outlined class="mb-3">
              Henüz alan eklenmedi. <b>Yeni Alan</b> butonuna basarak banner’a
              yazı ekleyebilirsin.
            </v-alert>
            <v-expansion-panels
              v-model="expandedFieldPanels"
              multiple
              focusable
            >
              <v-expansion-panel v-for="(r, idx) in fieldRows" :key="r.id">
                <v-expansion-panel-header class="py-2">
                  <div class="d-flex align-center" style="width: 100%">
                    <!-- details ikon -->
                    <v-icon small class="mr-2">mdi-tune</v-icon>

                    <!-- Alan no -->
                    <div class="text-subtitle-2 mr-2">Alan #{{ idx + 1 }}</div>

                    <!-- seçilen field etiketi -->
                    <div class="grey--text text--lighten-1">
                      ➤ {{ fieldLabel(r.field) }}
                    </div>

                    <!-- uygulandı mı? -->
                    <v-chip
                      v-if="isRowApplied(r)"
                      small
                      class="ml-2"
                      color="success"
                      text-color="white"
                    >
                      ✓
                    </v-chip>

                    <v-spacer />

                    <!-- sil -->
                    <v-btn icon color="error" @click.stop="removeFieldRow(idx)">
                      <v-icon>delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>

                <v-expansion-panel-content>
                  <!-- BURASI: senin mevcut kart içeriğin aynen -->
                  <!-- ✅ 1) Alan Seç -->
                  <v-row dense>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="r.field"
                        :items="quickFields"
                        item-text="label"
                        item-value="value"
                        label="Alan"
                        dense
                        outlined
                        @change="onFieldChange(r)"
                      />
                    </v-col>
                    <!-- ✅ 2) Değer Renk / Font / Size -->
                    <v-row dense>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="r.valueColor"
                          label="Değer Renk"
                          dense
                          outlined
                          @input="markDirtyLocal()"
                        >
                          <template v-slot:append>
                            <div
                              :style="{
                                width: '22px',
                                height: '22px',
                                borderRadius: '6px',
                                border: '1px solid rgba(255,255,255,0.25)',
                                background: r.valueColor || '#ffffff',
                                cursor: 'pointer',
                              }"
                              @click.stop="openColorPicker(r.id, 'value')"
                              title="Renk seç"
                            ></div>
                          </template>
                        </v-text-field>
                      </v-col>

                      <v-col cols="12" md="4">
                        <v-select
                          v-model="r.valueFont"
                          :items="fontFamilies"
                          label="Değer Font"
                          dense
                          outlined
                          @change="markDirtyLocal()"
                        />
                      </v-col>

                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model.number="r.valueSize"
                          type="number"
                          label="Değer Size"
                          dense
                          outlined
                          @input="markDirtyLocal()"
                        />
                      </v-col>
                    </v-row>

                    <!-- ✅ 3) Başlık Aç/Kapa Butonu -->
                    <v-row dense>
                      <v-col cols="12" md="4">
                        <v-switch
                          v-model="r.showLabel"
                          label="Başlık Göster"
                          dense
                          class="mt-0"
                          @change="
                            if (!r.showLabel) {
                              r.label = '';
                            }
                            markDirtyLocal();
                          "
                        />
                      </v-col>
                    </v-row>

                    <!-- ✅ 4) Başlık Yazısı -->
                    <v-row v-if="r.showLabel" dense>
                      <v-col cols="12" md="12">
                        <v-text-field
                          v-model="r.label"
                          label="Başlık Yazısı"
                          dense
                          outlined
                          @input="markDirtyLocal()"
                        />
                      </v-col>
                    </v-row>

                    <!-- ✅ 5) Başlık Renk / Font / Size -->
                    <v-row v-if="r.showLabel" dense>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="r.labelColor"
                          label="Başlık Renk"
                          dense
                          outlined
                          @input="markDirtyLocal()"
                        >
                          <template v-slot:append>
                            <div
                              :style="{
                                width: '22px',
                                height: '22px',
                                borderRadius: '6px',
                                border: '1px solid rgba(255,255,255,0.25)',
                                background: r.labelColor || '#ffffff',
                                cursor: 'pointer',
                              }"
                              @click.stop="openColorPicker(r.id, 'label')"
                              title="Renk seç"
                            ></div>
                          </template>
                        </v-text-field>
                      </v-col>

                      <v-col cols="12" md="4">
                        <v-select
                          v-model="r.labelFont"
                          :items="fontFamilies"
                          label="Başlık Font"
                          dense
                          outlined
                          @change="markDirtyLocal()"
                        />
                      </v-col>

                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model.number="r.labelSize"
                          type="number"
                          label="Başlık Size"
                          dense
                          outlined
                          @input="markDirtyLocal()"
                        />
                      </v-col>
                    </v-row>

                    <!-- ✅ 6) Başlık Konum + Başlık Hizalama -->
                    <!-- NOT: position/align her iki katmana da uygulanıyor (label + value) -->
                    <v-row dense>
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="r.position"
                          :items="positionItems"
                          item-text="label"
                          item-value="value"
                          label="Başlık Konum"
                          dense
                          outlined
                          @change="markDirtyLocal()"
                        />
                      </v-col>

                      <v-col cols="12" md="6">
                        <v-select
                          v-model="r.align"
                          :items="alignItems"
                          item-text="label"
                          item-value="value"
                          label="Başlık Hizalama"
                          dense
                          outlined
                          @change="markDirtyLocal()"
                        />
                      </v-col>
                    </v-row>

                    <!-- ✅ 7) Yatay / Dikey -->
                    <v-row dense>
                      <v-col cols="6" md="3">
                        <v-text-field
                          v-model.number="r.x"
                          type="number"
                          label="Yatay (X)"
                          dense
                          outlined
                          @input="markDirtyLocal()"
                        />
                      </v-col>

                      <v-col cols="6" md="3">
                        <v-text-field
                          v-model.number="r.y"
                          type="number"
                          label="Dikey (Y)"
                          dense
                          outlined
                          @input="markDirtyLocal()"
                        />
                      </v-col>

                      <!-- ✅ 8) Alan Göster/Gizle + Shadow -->
                      <v-col cols="6" md="3">
                        <v-switch
                          v-model="r.enabled"
                          label="Alan Göster"
                          dense
                          class="mt-0"
                          @change="markDirtyLocal()"
                        />
                      </v-col>

                      <v-col cols="6" md="3">
                        <v-switch
                          v-model="r.shadow"
                          label="Shadow"
                          dense
                          class="mt-0"
                          @change="markDirtyLocal()"
                        />
                      </v-col>
                    </v-row>

                    <!-- ✅ 9) Quotes textarea (sadece quote seçilince) -->
                    <v-textarea
                      v-if="r && r.field === 'quote'"
                      v-model="quotesText"
                      label="Özlü sözler (her satır 1 söz)"
                      rows="8"
                      outlined
                      dense
                      class="mt-2"
                      @input="markDirtyLocal()"
                    />
                  </v-row>

                  <!-- ✅ 10) Ekle / Güncelle + Katmandan Sil -->
                  <v-row dense class="mt-2">
                    <v-col cols="12" md="6">
                      <v-btn block color="primary" @click="applyRowToLayers(r)">
                        <v-icon left>add</v-icon> Ekle / Güncelle
                      </v-btn>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-btn
                        block
                        color="error"
                        outlined
                        @click="removeRowLayer(r)"
                      >
                        <v-icon left>delete</v-icon> Katmandan Sil
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <v-btn
              block
              color="primary"
              :disabled="!fieldRows.length || !localDirty"
              @click="syncRowsToLayers"
            >
              <v-icon left>done_all</v-icon> Tüm Alanları Katmana Uygula
            </v-btn>
          </v-card-text>
        </v-card>

        <!-- GENERAL -->
        <v-card class="mt-3">
          <v-card-title class="py-3">
            <span class="text-subtitle-1 font-weight-medium"
              >Genel Ayarlar</span
            >
            <v-spacer />
            <v-btn
              color="primary"
              :loading="saving"
              :disabled="!dirty"
              @click="save"
            >
              <v-icon left>save</v-icon> Kaydet
            </v-btn>
          </v-card-title>

          <v-divider />

          <v-card-text>
            <v-row dense>
              <v-col cols="12" md="4">
                <v-select
                  :items="bgModes"
                  item-text="text"
                  item-value="value"
                  v-model="cfg.banner.bg_mode"
                  label="Arkaplan Modu"
                  dense
                  outlined
                  @change="markDirty"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="cfg.banner.cache_ttl_ms"
                  type="number"
                  label="TS3 Cache (ms)"
                  dense
                  outlined
                  min="0"
                  @input="markDirty"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="cfg.banner.bg_glob_prefix"
                  label="BG prefix"
                  dense
                  outlined
                  placeholder="banner_"
                  @input="markDirty"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- RIGHT: Layers -->
      <v-col cols="12" md="3">
        <v-card>
          <v-card-title class="py-3">
            <span class="text-subtitle-1 font-weight-medium">Katmanlar</span>
            <v-spacer />

            <v-menu offset-y>
              <template #activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on" title="Katman ekle">
                  <v-icon>add</v-icon>
                </v-btn>
              </template>

              <v-list dense>
                <v-list-item @click="addLayer('rect')">
                  <v-list-item-icon
                    ><v-icon>crop_square</v-icon></v-list-item-icon
                  >
                  <v-list-item-title>Kutu (Rect)</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-card-title>

          <v-divider />

          <v-card-text style="max-height: 32vh; overflow: auto">
            <v-alert
              v-if="!layers.length"
              dense
              type="warning"
              text
              class="mb-3"
            >
              layout.items boş. + ile katman ekle.
            </v-alert>

            <v-list dense>
              <v-list-item
                v-for="(it, idx) in layers"
                :key="it.id || idx"
                :class="{ 'grey darken-3': selectedLayerIndex === idx }"
                @click="selectLayer(idx)"
                style="border-radius: 8px"
              >
                <v-list-item-content>
                  <v-list-item-title class="text-truncate">
                    <v-chip x-small class="mr-2" label>{{ it.type }}</v-chip>
                    {{ it.id || "layer_" + idx }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-truncate">
                    <span v-if="it.type === 'text'">{{ it.text }}</span>
                    <span v-else>Rect {{ it.w }}x{{ it.h }}</span>
                  </v-list-item-subtitle>
                </v-list-item-content>

                <v-list-item-action class="d-flex" style="gap: 6px">
                  <v-btn
                    icon
                    x-small
                    @click.stop="moveLayer(idx, -1)"
                    :disabled="idx === 0"
                    title="Yukarı"
                  >
                    <v-icon small>arrow_upward</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    x-small
                    @click.stop="moveLayer(idx, 1)"
                    :disabled="idx === layers.length - 1"
                    title="Aşağı"
                  >
                    <v-icon small>arrow_downward</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    x-small
                    @click.stop="toggleLayer(idx)"
                    :title="layers[idx].enabled === false ? 'Aç' : 'Kapat'"
                  >
                    <v-icon small>{{
                      layers[idx].enabled === false
                        ? "visibility_off"
                        : "visibility"
                    }}</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    x-small
                    color="error"
                    @click.stop="removeLayer(idx)"
                    title="Sil"
                  >
                    <v-icon small>delete</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card-text>

          <v-divider />

          <v-card-text v-if="selectedLayer">
            <div class="d-flex align-center mb-2">
              <div class="text-subtitle-2">Seçili Katman</div>
              <v-spacer />
              <v-chip x-small label>{{ selectedLayer.type }}</v-chip>
            </div>

            <v-text-field
              v-model="selectedLayer.id"
              label="ID"
              dense
              outlined
              @input="markDirty"
            />

            <v-switch
              v-model="layerEnabledProxy"
              label="Enabled"
              dense
              class="mt-0"
              @change="applyEnabledProxy"
            />

            <v-row dense>
              <v-col cols="6">
                <v-text-field
                  v-model.number="selectedLayer.x"
                  type="number"
                  label="X"
                  dense
                  outlined
                  @input="markDirty"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model.number="selectedLayer.y"
                  type="number"
                  label="Y"
                  dense
                  outlined
                  @input="markDirty"
                />
              </v-col>
            </v-row>

            <template v-if="selectedLayer.type === 'rect'">
              <v-row dense>
                <v-col cols="6">
                  <v-text-field
                    v-model.number="selectedLayer.w"
                    type="number"
                    label="W"
                    dense
                    outlined
                    @input="markDirty"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model.number="selectedLayer.h"
                    type="number"
                    label="H"
                    dense
                    outlined
                    @input="markDirty"
                  />
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="8">
                  <v-text-field
                    v-model="selectedLayer.fill"
                    label="Fill (rgba/hex)"
                    dense
                    outlined
                    @input="markDirty"
                  />
                </v-col>
                <v-col cols="4">
                  <v-text-field
                    v-model.number="selectedLayer.radius"
                    type="number"
                    label="Radius"
                    dense
                    outlined
                    @input="markDirty"
                  />
                </v-col>
              </v-row>
            </template>

            <v-divider class="my-3" />

            <v-btn
              block
              color="primary"
              @click="save"
              :loading="saving"
              :disabled="!dirty"
            >
              <v-icon left>save</v-icon> Kaydet
            </v-btn>

            <v-btn block text class="mt-2" @click="refreshPreview">
              <v-icon left>cached</v-icon> Önizlemeyi yenile
            </v-btn>
          </v-card-text>

          <v-card-text v-else>
            <v-alert dense type="info" text>Sağdan bir katman seç.</v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-dialog v-model="colorPicker.open" max-width="360" persistent>
      <v-card>
        <v-card-title class="py-2">
          {{ colorPickerTitle }}
          <v-spacer />
          <v-btn icon @click="closeColorPicker">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-color-picker
            v-model="colorPickerValue"
            mode="hexa"
            hide-mode-switch
            @input="onColorPicked"
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeColorPicker">Kapat</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :timeout="2500">
      {{ snackbar.text }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Kapat</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import draggable from "vuedraggable";
const SITE_ORIGIN =
  typeof window !== "undefined" && window.location
    ? window.location.origin
    : "";

const BASE = (process.env.VUE_APP_BASE_PATH || "").replace(/\/$/, "");

const APIABS = (p) =>
  `${SITE_ORIGIN}${BASE}${p.startsWith("/") ? "" : "/"}${p}`;
const API = (p) => `${BASE}${p.startsWith("/") ? "" : "/"}${p}`;

export default {
  name: "ServerBanner",
  components: { draggable },
  data() {
    return {
      loadingImages: false,
      currentPreviewBgName: "",
      previewUrl: "",
      _bgStateTimer: null,
      uploading: false,
      saving: false,
      error: "",
      flashBg: "",
      localDirty: false,
      quotesText: "",
      previewTick: 0,
      expandedFieldPanels: [],
      colorPicker: {
        open: false,
        rowId: null, // hangi field row
        target: null, // "label" | "value"
      },

      cfg: { ts3: {}, banner: {}, fonts: {}, layout: { items: [] } },

      images: [],
      imageFilter: "",
      selectedBg: "",

      selectedLayerIndex: -1,
      dirty: false,
      previewBuster: Date.now(),

      uploadFile: null,

      snackbar: { show: false, text: "" },
      rotateModes: [
        { text: "Rotate (dakikada değiş)", value: "rotate" },
        { text: "Seçili görsel sabit", value: "selected" },
      ],
      bgModes: [
        { text: "Rastgele Resim", value: "random" },
        { text: "Sırayla Resim", value: "sequential" },
        { text: "Sabit Resim", value: "selected" },
      ],

      quick: {
        field: "",
        style: "stack_title_top",
        x: 40,
        y: 40,
        size: 18,
        color: "#ffffff",
        font: "arial2",
      },

      quickFields: [
        { label: "Online", value: "online" },
        { label: "Saat", value: "time" },
        { label: "Tarih", value: "date" },
        { label: "Nickname", value: "uname" },
        { label: "IP", value: "ip" },
        { label: "Client UID", value: "uid" },
        { label: "Platform", value: "platform" },
        { label: "Bilgi (info)", value: "info" },
        { label: "Rastgele Yazı (Özlü Sözler)", value: "quote" },
      ],

      fieldRows: [],

      positionItems: [
        { label: "Altta", value: "bottom" },
        { label: "Üstte", value: "top" },
        { label: "Yan yana", value: "inline" },
      ],
      alignItems: [
        { label: "Sola yasla", value: "left" },
        { label: "Ortala", value: "center" },
        { label: "Sağa yasla", value: "right" },
      ],
    };
  },
  computed: {
    layers() {
      return this.cfg && this.cfg.layout && Array.isArray(this.cfg.layout.items)
        ? this.cfg.layout.items
        : [];
    },
    selectedLayer() {
      return this.selectedLayerIndex >= 0
        ? this.layers[this.selectedLayerIndex] || null
        : null;
    },
    layerEnabledProxy: {
      get() {
        if (!this.selectedLayer) return true;
        return this.selectedLayer.enabled !== false;
      },
      set() {},
    },
    fontFamilies() {
      const keys = Object.keys(this.cfg.fonts || {});
      return keys.length ? keys : ["arial2", "rex", "ttw", "antique", "lexend"];
    },
    filteredImages() {
      const q = (this.imageFilter || "").toLowerCase().trim();
      if (!q) return this.images;
      return this.images.filter((x) =>
        (x.name || "").toLowerCase().includes(q)
      );
    },
    previewBase() {
      return API("/api/public/banner.jpg");
    },
    cacheBustSuffix() {
      // thumbnails için cache bust
      return `?t=${this.previewBuster}`;
    },
    publicBannerUrl() {
      return APIABS("/api/public/banner.jpg");
    },

    colorPickerTitle() {
      if (!this.colorPicker || !this.colorPicker.target) return "Renk";
      return this.colorPicker.target === "label" ? "Başlık Renk" : "Değer Renk";
    },
    colorPickerValue: {
      get() {
        const r = this.fieldRows.find((x) => x.id === this.colorPicker.rowId);
        if (!r) return "#ffffff";
        return this.colorPicker.target === "label"
          ? r.labelColor || "#ffffff"
          : r.valueColor || "#ffffff";
      },
      set(v) {
        const r = this.fieldRows.find((x) => x.id === this.colorPicker.rowId);
        if (!r) return;
        if (this.colorPicker.target === "label") r.labelColor = v;
        else r.valueColor = v;
      },
    },
    selectedBgName: function () {
      // 1) selectedBg string ise direkt dosya adı
      if (this.selectedBg && typeof this.selectedBg === "string") {
        return this.selectedBg;
      }

      // 2) selectedBg obje ise
      if (this.selectedBg && this.selectedBg.name) {
        return this.selectedBg.name;
      }

      // 3) config'ten selected_bg varsa (path olabilir)
      if (this.cfg && this.cfg.banner && this.cfg.banner.selected_bg) {
        var s = String(this.cfg.banner.selected_bg);
        var parts = s.split("/");
        return parts[parts.length - 1];
      }

      return "";
    },
    previewImgSrc() {
      const base = this.previewUrl || this.previewBase;
      return base + "?t=" + String(this.previewTick || 0);
    },
  },
  methods: {
    toast(msg) {
      this.snackbar.text = msg;
      this.snackbar.show = true;
    },
    markDirty() {
      this.dirty = true;
    },
    refreshPreview() {
      this.previewTick = Date.now();
      this.fetchPreviewBgName();
    },

    applyEnabledProxy(val) {
      if (!this.selectedLayer) return;
      this.selectedLayer.enabled = !!val;
      this.markDirty();
    },

    selectBg(img) {
      this.selectedBg = img.name;

      // SADECE seçildi toast (mod değiştirme yok)
      this.toast("Seçildi: " + img.name);

      // preview yenileme de istemiyorsan kapat
      // this.refreshPreview();
    },

    selectLayer(idx) {
      this.selectedLayerIndex = idx;
    },
    moveLayer(idx, dir) {
      const arr = this.layers;
      const ni = idx + dir;
      if (ni < 0 || ni >= arr.length) return;
      const tmp = arr[idx];
      this.$set(arr, idx, arr[ni]);
      this.$set(arr, ni, tmp);
      this.selectedLayerIndex = ni;
      this.markDirty();
      this.refreshPreview();
    },
    toggleLayer(idx) {
      const it = this.layers[idx];
      if (!it) return;
      it.enabled = !(it.enabled !== false);
      this.markDirty();
      this.refreshPreview();
    },
    removeLayer(idx) {
      this.layers.splice(idx, 1);
      if (this.selectedLayerIndex >= this.layers.length) {
        this.selectedLayerIndex = this.layers.length - 1;
      }
      this.markDirty();
      this.refreshPreview();
    },
    addLayer(type) {
      const id = `${type}_${Date.now()}`;
      if (!this.cfg.layout) this.cfg.layout = { items: [] };
      if (!Array.isArray(this.cfg.layout.items)) this.cfg.layout.items = [];

      if (type === "rect") {
        this.cfg.layout.items.push({
          id,
          type: "rect",
          x: 20,
          y: 20,
          w: 500,
          h: 46,
          fill: "rgba(0,0,0,0.55)",
          radius: 10,
          enabled: true,
        });
      } else {
        this.cfg.layout.items.push({
          id,
          type: "text",
          x: 30,
          y: 50,
          text: "{time}",
          font: "arial2",
          size: 18,
          color: "#ffffff",
          shadow: true,
          lineHeight: 22,
          enabled: true,
        });
      }

      this.selectedLayerIndex = this.cfg.layout.items.length - 1;
      this.markDirty();
      this.refreshPreview();
    },

    // QUICK ADD: field + style => 1 layer (multiline) oluşturur
    addQuickField() {
      const f = this.quick.field;
      const found = this.quickFields.find((x) => x.value === f);
      const label = found && found.label ? found.label : f;
      const token = `{${f}}`;

      let text = "";
      if (this.quick.style === "stack_title_top") text = `${label}\n${token}`;
      if (this.quick.style === "stack_value_top") text = `${token}\n${label}`;
      if (this.quick.style === "inline_title_left") text = `${label} ${token}`;
      if (this.quick.style === "inline_value_left") text = `${token} ${label}`;

      // okunabilirlik için otomatik arka bar da ekleyelim (çok iyi duruyor)
      const rectId = `rect_${f}_${Date.now()}`;
      const textId = `text_${f}_${Date.now()}`;

      // rect ekle (textin arkasına)
      this.layers.push({
        id: rectId,
        type: "rect",
        x: this.quick.x - 10,
        y: this.quick.y - (this.quick.style.includes("stack") ? 36 : 26),
        w: 260,
        h: this.quick.style.includes("stack") ? 56 : 40,
        fill: "rgba(0,0,0,0.55)",
        radius: 10,
        enabled: true,
      });

      // text ekle
      this.layers.push({
        id: textId,
        type: "text",
        x: this.quick.x,
        y: this.quick.y,
        text,
        font: this.quick.font,
        size: this.quick.size,
        color: this.quick.color,
        shadow: true,
        lineHeight: this.quick.size + 6,
        enabled: true,
      });

      this.selectedLayerIndex = this.layers.length - 1;
      this.markDirty();
      this.refreshPreview();
      this.toast(`${label} eklendi.`);
    },

    async loadAll() {
      this.error = "";
      try {
        const cfgRes = await fetch(API("/api/banner/config"), {
          credentials: "include",
        });
        if (!cfgRes.ok) throw new Error("Config alınamadı.");
        const cfg = await cfgRes.json();

        if (!cfg.layout) cfg.layout = { items: [] };
        if (!cfg.layout.items) cfg.layout.items = [];
        if (!cfg.banner) cfg.banner = {};
        if (!cfg.fonts) cfg.fonts = {};

        this.cfg = cfg;
        this.rebuildFieldRowsFromCfg();
        this.quotesText = Array.isArray(this.cfg.banner.quotes)
          ? this.cfg.banner.quotes.join("\n")
          : "";

        // quick defaults
        this.quick.font = this.fontFamilies[0] || "arial2";

        await this.reloadImages();

        if (this.layers.length && this.selectedLayerIndex === -1)
          this.selectedLayerIndex = 0;

        this.dirty = false;
        this.refreshPreview();
      } catch (e) {
        this.error = String(e && e.message ? e.message : e);
      }
    },

    async reloadImages() {
      this.loadingImages = true;
      try {
        const res = await fetch(API("/api/banner/images"), {
          credentials: "include",
        });
        if (!res.ok) throw new Error("Resimler alınamadı.");
        const json = await res.json();
        this.images = (json.files || []).map((f) => ({
          name: f.name,
          url: f.url,
        }));
      } catch (e) {
        this.toast(String(e && e.message ? e.message : e));
      } finally {
        this.loadingImages = false;
      }
    },

    async uploadImage() {
      if (!this.uploadFile) return;
      this.uploading = true;
      try {
        const fd = new FormData();
        fd.append("file", this.uploadFile);

        const res = await fetch(API("/api/banner/upload"), {
          method: "POST",
          credentials: "include",
          body: fd,
        });

        if (!res.ok) throw new Error("Upload başarısız.");
        this.uploadFile = null;
        await this.reloadImages();
        this.refreshPreview();
        this.toast("Yüklendi.");
      } catch (e) {
        this.toast(String(e && e.message ? e.message : e));
      } finally {
        this.uploading = false;
      }
    },

    async deleteImage(img) {
      if (!img || !img.name) return;
      const ok = confirm(`Silinsin mi?\n${img.name}`);
      if (!ok) return;

      try {
        const res = await fetch(
          API(`/api/banner/images/${encodeURIComponent(img.name)}`),
          {
            method: "DELETE",
            credentials: "include",
          }
        );
        if (!res.ok) throw new Error("Silinemedi.");
        await this.reloadImages();
        this.refreshPreview();
        this.toast("Silindi.");
      } catch (e) {
        this.toast(String(e && e.message ? e.message : e));
      }
    },

    async save() {
      this.saving = true;
      this.error = "";
      if (!this.cfg.banner) this.cfg.banner = {};
      this.cfg.banner.quotes = String(this.quotesText || "")
        .split("\n")
        .map((x) => x.trim())
        .filter(Boolean);
      try {
        const res = await fetch(API("/api/banner/config"), {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(this.cfg),
        });
        if (!res.ok) throw new Error("Kaydetme başarısız.");
        this.dirty = false;
        this.toast("Kaydedildi.");
        this.refreshPreview();
      } catch (e) {
        this.error = String(e && e.message ? e.message : e);
      } finally {
        this.saving = false;
      }
    },
    async saveImageOrder() {
      try {
        const order = this.images.map((x) => x.name);

        const res = await fetch(API("/api/banner/order"), {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ order }),
        });

        if (!res.ok) {
          const t = await res.text().catch(() => "");
          throw new Error(t || "Sıra kaydedilemedi.");
        }

        this.toast("Sıralama kaydedildi ✅");
      } catch (e) {
        this.toast(String(e && e.message ? e.message : e));
      }
    },
    markDirtyLocal() {
      this.localDirty = true;
    },
    addFieldRow() {
      this.fieldRows.push({
        id: "row_" + Date.now() + "_" + Math.floor(Math.random() * 9999),
        field: "uname",
        label: "Kullanıcı Adı",
        layout: "stack_title_top",
        color: "#ffffff",
        font: this.fontFamilies[0] || "arial2",
        size: 18,
        x: 40,
        y: 40,
        enabled: true,
        shadow: true,
        position: "bottom", // bottom | top | inline
        align: "left", // left | center | right
        labelFont: "arial2",
        labelSize: 14,
        valueFont: "rex",
        valueSize: 18,
        labelColor: "#ffffff",
        valueColor: "#ffffff",
        showLabel: true,
      });
      this.localDirty = true;
      this.$nextTick(() => {
        const idx = this.fieldRows.length - 1;
        const arr = Array.isArray(this.expandedFieldPanels)
          ? [...this.expandedFieldPanels]
          : [];
        if (!arr.includes(idx)) arr.push(idx);
        this.expandedFieldPanels = arr;
      });
    },
    removeFieldRow(idx) {
      const r = this.fieldRows[idx];
      if (!r) return;

      // ✅ önce cfg.layout.items içindeki bağlı katmanları temizle
      if (this.cfg && this.cfg.layout && Array.isArray(this.cfg.layout.items)) {
        const rowId = r.id;

        this.cfg.layout.items = this.cfg.layout.items.filter((it) => {
          // field meta ile oluşturulan label/value katmanlarını sil
          return !(
            it &&
            it.meta &&
            it.meta.kind === "field" &&
            it.meta.rowId === rowId
          );
        });
      }

      // sonra row'u listeden kaldır
      this.fieldRows.splice(idx, 1);

      // UI güncelle
      this.localDirty = true;
      this.markDirty();
      this.refreshPreview();
      this.toast("Alan silindi.");
    },

    autoLabel(r) {
      var found = this.quickFields.find(function (x) {
        return x.value === r.field;
      });
      r.label = found && found.label ? found.label : r.field;
    },
    rowText(r) {
      var token = "{" + r.field + "}";
      if (r.position === "top") return token + "\n" + r.label; // üstte değer
      if (r.position === "bottom") return r.label + "\n" + token; // altta değer
      return r.label + " " + token; // yan yana
    },

    // Bu row için text layer id üretelim (stabil)
    rowLayerId(r) {
      return "field_" + r.id;
    },

    applyRowToLayers(r, idx) {
      if (!this.cfg.layout) this.cfg.layout = { items: [] };
      if (!Array.isArray(this.cfg.layout.items)) this.cfg.layout.items = [];

      const baseId = "field_" + r.id;
      const items = this.cfg.layout.items;

      // LABEL layer
      const labelId = baseId + "_label";
      const valueId = baseId + "_value";

      const labelText = r.showLabel ? r.label || "" : "";
      const valueText = "{" + r.field + "}";

      const gap = 6;

      let labelX = r.x;
      let labelY = r.y;
      let valueX = r.x;
      let valueY = r.y;

      if (r.position === "top") {
        valueY = r.y;
        labelY = r.y + r.valueSize + gap;
      } else if (r.position === "bottom") {
        labelY = r.y;
        valueY = r.y + r.labelSize + gap;
      } else {
        // inline
        labelX = r.x;
        valueX = r.x + r.labelSize * (labelText.length + 1);
      }

      const labelPayload = {
        id: labelId,
        type: "text",
        text: labelText,
        x: labelX,
        y: labelY,
        font: r.labelFont,
        size: r.labelSize,
        color: r.labelColor || "#ffffff",
        align: r.align,
        shadow: !!r.shadow,
        enabled: r.enabled !== false && !!r.showLabel,
        meta: {
          kind: "field",
          rowId: r.id,
          part: "label",
          row: JSON.parse(JSON.stringify(r)),
        },
      };

      const valuePayload = {
        id: valueId,
        type: "text",
        text: valueText,
        x: valueX,
        y: valueY,
        font: r.valueFont,
        size: r.valueSize,
        color: r.valueColor || "#ffffff",
        align: r.align,
        shadow: !!r.shadow,
        enabled: r.enabled !== false,
        meta: {
          kind: "field",
          rowId: r.id,
          part: "value",
          row: JSON.parse(JSON.stringify(r)),
        },
      };

      const upsert = (p) => {
        const i = items.findIndex((x) => x.id === p.id);
        if (i >= 0) items[i] = Object.assign(items[i], p);
        else items.push(p);
      };

      // Başlık kapalıysa: label layer'ı config'ten kaldır
      if (!r.showLabel) {
        const li = items.findIndex((x) => x && x.id === labelId);
        if (li >= 0) items.splice(li, 1);
      } else {
        upsert(labelPayload);
      }

      upsert(valuePayload);

      this.markDirty();
      this.refreshPreview();
      this.toast("Uygulandı: " + r.label);
      if (typeof idx === "number") this.collapsePanel(idx);
    },

    removeRowLayer(r) {
      if (!this.cfg.layout || !Array.isArray(this.cfg.layout.items)) return;

      const rowId = r.id;
      this.cfg.layout.items = this.cfg.layout.items.filter((it) => {
        return !(
          it &&
          it.meta &&
          it.meta.kind === "field" &&
          it.meta.rowId === rowId
        );
      });

      this.markDirty();
      this.refreshPreview();
      this.toast("Katmandan silindi: " + (r.label || rowId));
    },

    syncRowsToLayers() {
      for (var i = 0; i < this.fieldRows.length; i++) {
        this.applyRowToLayers(this.fieldRows[i]);
      }
      this.localDirty = false;
    },
    rebuildFieldRowsFromCfg() {
      try {
        const items =
          this.cfg && this.cfg.layout && Array.isArray(this.cfg.layout.items)
            ? this.cfg.layout.items
            : [];

        // meta.kind === "field" olanları topla
        const fieldItems = items.filter(
          (it) =>
            it &&
            it.meta &&
            it.meta.kind === "field" &&
            it.meta.row &&
            it.meta.row.id
        );

        // rowId bazlı tekilleştir
        const byId = {};
        for (const it of fieldItems) {
          const row = it.meta.row;
          byId[row.id] = row; // aynı row iki layer’da da var; biri yeter
        }

        // sırayı stabil tutmak için id'ye göre sıralayalım (istersen x/y’ye göre de olur)
        const rows = Object.values(byId).sort((a, b) =>
          String(a.id).localeCompare(String(b.id))
        );

        this.fieldRows = rows;
        this.localDirty = false;
      } catch (e) {
        // bozulursa sessizce boş başlat
        this.fieldRows = [];
      }
    },
    copyBannerUrl() {
      const txt = this.publicBannerUrl;

      // 1) Modern API (HTTPS/permission uygunsa)
      try {
        if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard
            .writeText(txt)
            .then(() => this.toast("Kopyalandı ✅"))
            .catch(() => this.fallbackCopy(txt));
          return;
        }
      } catch (e) {
        // ignore -> fallback
      }

      // 2) Fallback
      this.fallbackCopy(txt);
    },

    fallbackCopy(txt) {
      try {
        const el = document.createElement("textarea");
        el.value = txt;
        el.setAttribute("readonly", "");
        el.style.position = "fixed";
        el.style.top = "-9999px";
        el.style.left = "-9999px";
        document.body.appendChild(el);

        el.select();
        el.setSelectionRange(0, el.value.length);

        const ok = document.execCommand("copy");
        document.body.removeChild(el);

        this.toast(ok ? "Kopyalandı ✅" : "Kopyalanamadı ❌");
      } catch (e) {
        this.toast("Kopyalanamadı ❌");
      }
    },

    openBannerUrl() {
      window.open(this.publicBannerUrl, "_blank");
    },
    openColorPicker(rowId, target) {
      this.colorPicker.rowId = rowId;
      this.colorPicker.target = target;
      this.colorPicker.open = true;
    },
    closeColorPicker() {
      this.colorPicker.open = false;
    },
    onColorPicked() {
      this.markDirtyLocal();
    },
    async fetchPreviewBgName() {
      const stateCandidates = [API("/api/public/banner_state")];
      const imgCandidates = [API("/api/public/banner.jpg")];

      // 1) banner_state dene
      for (const url of stateCandidates) {
        try {
          const res = await fetch(url, {
            method: "GET",
            credentials: "include",
          });
          if (!res.ok) continue;

          const j = await res.json();
          if (j && j.ok) {
            const newName = j.bgName || "";
            const oldName = this.currentPreviewBgName || "";

            // bgName güncelle
            this.currentPreviewBgName = newName;

            // ✅ resim endpointini de doğru seç (public / publicsız)
            if (!this.previewUrl) {
              this.previewUrl = imgCandidates[0];
            }

            // ✅ sadece gerçekten değiştiyse cache kır -> resim yenilensin
            if (newName && newName !== oldName) {
              this.previewTick = Date.now();
            }

            return;
          }
        } catch (e) {}
      }

      // 2) Eğer state 404 ise spam basma: timer'ı kapat
      if (this._bgStateTimer) {
        clearInterval(this._bgStateTimer);
        this._bgStateTimer = null;
      }
    },

    scrollToPreviewBg() {
      const name = this.currentPreviewBgName;
      if (!name) return;

      // solda seçili göster
      this.selectedBg = name;

      const refKey = "thumb_" + name;
      const elArr = this.$refs[refKey];
      const el = Array.isArray(elArr) ? elArr[0] : elArr;

      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      } else if (el && el.$el && el.$el.scrollIntoView) {
        el.$el.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      this.flashBg = name;
      setTimeout(() => {
        if (this.flashBg === name) this.flashBg = "";
      }, 900);
    },
    fieldLabel(fieldVal) {
      const f = this.quickFields.find((x) => x.value === fieldVal);
      return f && f.label ? f.label : fieldVal || "";
    },

    isRowApplied(r) {
      if (
        !this.cfg ||
        !this.cfg.layout ||
        !Array.isArray(this.cfg.layout.items)
      )
        return false;
      return this.cfg.layout.items.some(
        (it) =>
          it && it.meta && it.meta.kind === "field" && it.meta.rowId === r.id
      );
    },

    collapsePanel(idx) {
      // multiple olduğu için array
      const arr = Array.isArray(this.expandedFieldPanels)
        ? [...this.expandedFieldPanels]
        : [];
      const i = arr.indexOf(idx);
      if (i >= 0) arr.splice(i, 1);
      this.expandedFieldPanels = arr;
    },
    onFieldChange(r) {
      this.autoLabel(r); // sende zaten var
      this.markDirtyLocal();
    },
  },
  mounted() {
    this.previewTick = Date.now();
    this.loadAll();
    this.fetchPreviewBgName();

    // 5 sn'de bir güncelle (banner dakikada bir dönüyor ama biz hızlı göstersin diye 5 sn)
    this._bgStateTimer = setInterval(() => {
      this.fetchPreviewBgName();
    }, 15000);
  },
  beforeDestroy() {
    if (this._bgStateTimer) clearInterval(this._bgStateTimer);
  },
};
</script>

<style scoped>
.caption {
  font-size: 11px;
}
.banner-thumb {
  user-select: none;
}
.drag-ghost {
  opacity: 0.6;
}
.drag-chosen {
  outline: 2px dashed rgba(255, 255, 255, 0.35);
}
.drag-dragging {
  cursor: grabbing !important;
}
.banner-flash {
  box-shadow: 0 0 18px rgba(239, 185, 11, 0.85) !important;
  border: 1px solid rgba(239, 185, 11, 0.9) !important;
  transition: box-shadow 0.2s ease;
}
</style>
